import * as Sentry from '@sentry/nextjs';
import { addDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from 'next';

import { getCurrentISOString } from 'utils/date.utils';
import { getDefaultLocale } from 'utils/i18n.utils';
import { generateStaticPaths, replacePathParams } from 'utils/routes.utils';

import { Duration } from 'constants/duration.constants';
import { NOT_FOUND } from 'constants/nextjs.constants';
import { Routes } from 'constants/routes.constants';
import { moreEventsQuery, pageEventQuery } from 'queries/events/queries';
import { fetchAllQueries } from 'queries/utils';
import { ticketsLowestPriceQuery } from 'restful/tickets';
import { datoClient } from 'services/api';
import Prefetcher from 'services/prefetcher';
import {
  AllPageEventSlugsDocument,
  EventCardFragment,
} from 'types/api/dato-operations.types';

import EventDetailContainer from 'components/@events/EventDetailContainer';
import { isEventTicketingEnabled } from 'components/@events/utils';
import EventSeo from 'components/@seo/EventSeo';

const EventDetail: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  event,
  moreEvents,
  eventLowestPrice,
  ticketingError,
}) => {
  return (
    <>
      <EventSeo event={event} tags={event.seoTags} />
      <EventDetailContainer
        // Ensures that nextjs remounts the component so all our hooks fire again
        key={event.id}
        event={event}
        city={event.venue.city.name}
        venue={event.venue}
        moreEvents={moreEvents}
        eventLowestPrice={eventLowestPrice}
        ticketingError={ticketingError}
      />
    </>
  );
};

export default EventDetail;

export const getStaticPaths: GetStaticPaths = async ({
  locales,
  defaultLocale,
}) => {
  const locale = getDefaultLocale({ defaultLocale });

  const allPageEvents = await fetchAllQueries(
    (nextParams) =>
      datoClient().request(AllPageEventSlugsDocument, {
        locale,
        filter: { endDatetime: { gte: getCurrentISOString() } },
        ...nextParams,
      }),
    { groupBy: 'allPageEvents' },
  );

  const paths = generateStaticPaths(allPageEvents, locales, ({ slug }) =>
    replacePathParams(Routes.EventDetail, { slug }),
  );

  return { paths, fallback: 'blocking' };
};

export const getStaticProps = async ({
  locale,
  params,
  preview,
}: GetStaticPropsContext<{ slug: string }>) => {
  const prefetcher = await Prefetcher.init({ locale, preview });
  const slug = params?.slug;

  if (!slug) return { ...NOT_FOUND };

  const { pageEvent } = await prefetcher.prefetch(pageEventQuery, {
    filter: { slug: { eq: slug } },
  });

  if (!pageEvent) return { ...NOT_FOUND };

  let moreEvents: Array<EventCardFragment> = [];
  if (pageEvent.startDatetime) {
    const startDatetime = toZonedTime(
      new Date(pageEvent.startDatetime),
      pageEvent.timezone,
    );
    const threeDaysPast = addDays(startDatetime, -3).toISOString();
    const data = await prefetcher.prefetch(moreEventsQuery, {
      filter: {
        startDatetime: {
          gte: threeDaysPast,
        },
        endDatetime: { gte: getCurrentISOString() },
        slug: { neq: slug },
        unlistedEvent: { eq: false },
      },
      slug,
    });
    moreEvents = data.moreEvents ?? [];
  }

  let ticketsLowestPrice = null;
  let ticketingError = false;

  try {
    if (isEventTicketingEnabled(pageEvent)) {
      const { eventLowestPrice } = await prefetcher.prefetch(
        ticketsLowestPriceQuery,
        {
          id: pageEvent.ticketsEventId,
          timeslotId: pageEvent.ticketsTimeslotId,
        },
      );
      ticketsLowestPrice = eventLowestPrice;
    }
  } catch (e) {
    ticketingError = true;
    console.error(e);
    Sentry.captureException(e);
  }

  return {
    props: {
      event: pageEvent,
      moreEvents: moreEvents,
      eventLowestPrice: ticketsLowestPrice,
      ticketingError,
      dehydratedState: prefetcher.dehydratedState,
    },
    revalidate: Duration.TEN_MIN,
  };
};
